import { TeamPlanTier } from "@alch/dx-entities";
import orderBy from "lodash/orderBy";
import { TeamMonthlyPlan } from "../redux/team";

/**
 * Converts the given number of CUs to USD based on the tiered pricing config.
 */
export function convertTieredCUsToUSD(
  onDemandCUs: number,
  tieredPricingConfigs: TieredPricingConfig[],
): number {
  let totalPrice = 0;
  let unpricedCUs = onDemandCUs;

  const orderedConfigs = orderBy(tieredPricingConfigs, "min", "desc");
  for (const { min, max, price } of orderedConfigs) {
    if (unpricedCUs > min && unpricedCUs <= max) {
      totalPrice += (unpricedCUs - min) * price;
      unpricedCUs = min;
    }
  }

  return totalPrice;
}

/**
 * Converts the given USD amount to CUs based on the tiered pricing config.
 */
export function convertTieredUSDToCUs(
  usd: number,
  tieredPricingConfigs: TieredPricingConfig[],
): number {
  let totalCUs = 0;
  let unpricedUSD = usd;

  const orderedConfigs = orderBy(tieredPricingConfigs, "min", "asc");
  for (const { min, max, price } of orderedConfigs) {
    if (unpricedUSD > 0) {
      const cuPrice = Math.min(unpricedUSD / price, max - min);
      totalCUs += cuPrice;
      unpricedUSD -= cuPrice * price;
    }
  }

  return totalCUs;
}

/**
 * Returns tiered pricing configs
 */
export function getTeamTieredPricingConfigs({
  teamId,
  activePlan,
}: {
  teamId: number;
  activePlan: Pick<TeamMonthlyPlan, "tier" | "overage_price_per_cu">;
}): TieredPricingConfig[] {
  const teamTieredCuPricingOverride = teamId
    ? TEAM_ID_TIERED_CU_PRICING_OVERRIDES[teamId]
    : null;
  const planTieredCuPricing = TIERED_CU_PRICING[activePlan.tier];
  const defaultTieredCuPricing = [
    { min: 1, max: Infinity, price: +activePlan.overage_price_per_cu },
  ];

  return (
    teamTieredCuPricingOverride ?? planTieredCuPricing ?? defaultTieredCuPricing
  );
}

/**
 * Tiered pricing configuration for a specific range of on-demand CUs.
 */
export interface TieredPricingConfig {
  min: number;
  max: number;
  price: number;
}

/**
 * Tiered pricing for on-demand CUs per active plan tier.
 */
export const TIERED_CU_PRICING: Partial<
  Record<TeamPlanTier, TieredPricingConfig[]>
> = {
  [TeamPlanTier.GROWTH]: [{ min: 0, max: Infinity, price: 0.0000012 }],
  [TeamPlanTier.SCALE]: [{ min: 0, max: Infinity, price: 0.000001 }],
  [TeamPlanTier.PAYG]: [
    { min: 0, max: 300_000_000, price: 0.45 * 1e-6 },
    { min: 300_000_000, max: Infinity, price: 0.4 * 1e-6 },
  ],
};

/**
 * Configures an override for tiered pricing for a specific team id.
 */
const TEAM_ID_TIERED_CU_PRICING_OVERRIDES: Record<
  number, // Team ID
  TieredPricingConfig[]
> = {
  2: [
    // OpenSea
    { min: 0, max: 25_000_000_000, price: 0.00000063 },
    { min: 25_000_000_000, max: Infinity, price: 0.000001 },
  ],
};
